import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Field, MessageBar, MessageBarBody, Select, Text, makeStyles } from '@fluentui/react-components';
import { tokens } from '@vigeo/ui';
import { fetchControlledForms, issueControlledForm } from '@/lib/api.js';

const useStyles = makeStyles({ root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM } });

/** Choices and role applicability come from published definitions, never a UI form list. */
export function ControlledFormIssuer({ personId, canEdit }: { readonly personId: string; readonly canEdit: boolean }) {
  const styles = useStyles();
  const client = useQueryClient();
  const [selectedId, setSelectedId] = useState('');
  const choices = useQuery({ queryKey: ['controlled-forms', personId], queryFn: () => fetchControlledForms(personId) });
  const selected = choices.data?.forms.find((form) => form.definitionId === selectedId);
  const issue = useMutation({ mutationFn: async () => {
    if (selected === undefined) throw new Error('Choose a published form.');
    return issueControlledForm(personId, { definitionId: selected.definitionId, version: selected.version });
  }, onSuccess: async () => {
    await Promise.all([client.invalidateQueries({ queryKey: ['controlled-forms', personId] }), client.invalidateQueries({ queryKey: ['checklist', personId] })]);
  } });
  return <div className={styles.root}>
    <Text weight="semibold">Issue a controlled form</Text>
    <Text>Choose an additional published form for this person. Their existing forms keep the versions originally issued.</Text>
    {choices.isError || issue.isError ? <MessageBar intent="error"><MessageBarBody>{String((choices.error ?? issue.error)?.message)}</MessageBarBody></MessageBar> : null}
    <Field label="Published form"><Select value={selectedId} disabled={choices.isLoading || issue.isPending} onChange={(_, data) => { setSelectedId(data.value); issue.reset(); }}>
      <option value="">Choose a form</option>
      {choices.data?.forms.map((form) => <option key={form.definitionId} value={form.definitionId}>{form.label} · version {form.version}{form.existingInstanceId === null ? '' : ' · already issued'}</option>)}
    </Select></Field>
    {choices.data?.forms.length === 0 ? <Text>No published forms are available for these roles.</Text> : null}
    {issue.isSuccess ? <MessageBar intent="success"><MessageBarBody>The form is available in the candidate’s existing portal.</MessageBarBody></MessageBar> : null}
    {canEdit ? <Button appearance="primary" disabled={selected === undefined || selected.existingInstanceId !== null || issue.isPending} onClick={() => issue.mutate()}>{issue.isPending ? 'Issuing…' : 'Issue selected form'}</Button> : null}
  </div>;
}
